<script setup>
import { ref, inject, computed } from 'vue';
import { useCompoundStore } from '@/stores/compound';
import { storeToRefs } from 'pinia';
import { trans } from 'laravel-vue-i18n';
import { useToast } from 'vue-toastification';

const { search, destroy, copy } = useCompoundStore();
const { all } = storeToRefs(useCompoundStore());
const toast = useToast();

const params = ref({
    term: '',
    page: 1,
    status: null,
    sort_by: null,
    compound_type_id: null,
    base_product_id: [],
    supply_product_id: [],
    per_page: 10,
});

const searchCompounds = () => {
    search({
        ...params.value,
    });
};

const modals = inject('modals');

const clearSearch = () => {
    params.value.term = '';
    searchCompounds();
};

const tableUpdate = ({ page, sortBy, itemsPerPage }) => {
    Object.assign(params.value, { page, sort_by: sortBy, per_page: itemsPerPage });
    searchCompounds();
};

const remove = async (compound) => {
    const confirm = await modals.show('ConfirmationModal', {
        props: {
            title: trans('label.confirm_delete'),
            prompt: trans('label.are_you_sure_delete'),
        }
    });

    if (confirm) {
        try {
            await destroy({ hash: compound.hash });
            searchCompounds();
            toast.success(trans('action.delete.success'));
        } catch (error) {
            toast.error(trans('action.delete.error'));
        }
        
    }
};

const copyCompound = async (compound) => {
    try {
        await copy({
            hash: compound.hash,
        });
        toast.success(trans('actions.copy.success', { type: compound.name }));
    } catch (error) {
        toast.error(trans('actions.copy.error', { type: compound.name }));
    }
    searchCompounds();
};

const openModal = (modal, mode = 'Create', compound = null) => {
    modals.show(modal, {
        props: compound ? {
            compound: compound,
            mode: mode
        } : {
            mode: mode,
        }
    }).then(() => {
        searchCompounds();
    });
};

import { useOptionsStore } from '@/stores/options';
const optionsStore = useOptionsStore();
const { compoundTypeOptions, labProducts } = storeToRefs(optionsStore);

const supplyProducts = computed(() => {
    return labProducts.value
        .filter(product => product.type === 'Supply')
        .sort((a, b) => a.label.localeCompare(b.label));
});

const baseProducts = computed(() => {
    return labProducts.value
        .filter(product => product.type === 'Base')
        .sort((a, b) => a.label.localeCompare(b.label));
});

const activeIngredientProducts = computed(() => {
    return labProducts.value
        .filter(product => product.type === 'Active')
        .sort((a, b) => a.label.localeCompare(b.label));
});

</script>

<template>
<MainContentLayout>
    <template #breadcrumb><BreadCrumb /></template>
    <template #title>{{ $t('label.management_title',{ 'what' : 'Compound' }) }}</template>
    <template #actions>
        <StyledButton @click="openModal('CompoundsModal')">
            <FontAwesomeIcon icon="plus" class="mr-1" /> {{ $t('label.create') }}
        </StyledButton>
    </template>
    <div class="grid lg:grid-cols-3 md:gap-4 md:mb-4">
        <StyledSearch
            v-model="params.term"
            v-debounce:500ms="searchCompounds"
            class="col-span-1"
            name="search"
            :label="$t('label.search')"
            :debounce-events="['input']"
            @click:clear="clearSearch" />
        <StyledDropdown
            v-model="params.status"
            clearable
            searchable
            :placeholder="$t('label.status')"
            :options="[
                { label: $t('label.active'), value: true },
                { label: $t('label.inactive'), value: false },
            ]"
            name="status"
            @click:clear="searchCompounds"
            @input="searchCompounds" />
        <StyledDropdown
            v-model="params.compound_type_id"
            clearable
            searchable
            :placeholder="$t('label.compound_type')"
            :options="compoundTypeOptions"
            name="compound_type"
            @click:clear="searchCompounds"
            @input="searchCompounds" />
        <StyledAutoComplete
            v-model="params.active_ingredient_product_id"
            clearable
            multiple
            :label="$t('label.active')"
            :placeholder="$t('label.active_ingredient')"
            :options="activeIngredientProducts"
            name="active_ingredient_product"
            :loading="loading"
            @click:clear="searchCompounds"
            @input="searchCompounds" />
        <StyledAutoComplete
            v-model="params.base_product_id"
            clearable
            multiple
            :label="$t('label.base')"
            :placeholder="$t('label.base')"
            :options="baseProducts"
            name="base_product"
            @click:clear="searchCompounds"
            @input="searchCompounds" />
        <StyledAutoComplete
            v-model="params.supply_product_id"
            clearable
            multiple
            :label="$t('label.supply')"
            :placeholder="$t('label.supply')"
            :options="supplyProducts"
            name="supply_product"
            @click:clear="searchCompounds"
            @input="searchCompounds" />
    </div>
    <DataTable
        v-if="all"
        :data="all"
        :headers="[
            { title: $t('label.id'), key: 'id' },
            { title: $t('label.name'), key: 'name' },
            { title: $t('label.compound_type'), key: 'type', value: item => `${item.compound_type.display_name}` },
            { title: $t('label.ingredient_price_per_100_units'), key: 'IngredientPrice', value: item => `$${item.IngredientPrice}` },
            { title: $t('label.status'), key: 'is_active', value: item => item.is_active ? $t('label.active') : $t('label.inactive') },
        ]"
        :actions="[
            {
                title: $t('label.Edit'),
                icon: 'mdi-pencil',
                action: (item) => openModal('CompoundsModal', 'Edit', item),
            },
            {
                title: $t('label.Delete'),
                icon: 'mdi-delete',
                action: (item) => remove(item),
            },
            {
                title: $t('label.Copy'),
                icon: 'mdi-content-copy',
                action: (item) => copyCompound(item),
            },
        ]"
        @table-update="tableUpdate" />
        
</MainContentLayout>
</template>
